import { useState } from 'react';
import { AlertTriangle, Layers } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  useQuarantine,
  useDecideQuarantine,
  useFailedOutbox,
  useReplayOutbox,
  useReplayAllOutbox,
  useStrandedAvatars,
  useOrgStatusUnacknowledged,
} from '../hooks/useAdminOps';
import {
  type FailedOutboxItem,
  type OrgStatusUnacknowledgedItem,
  type QuarantineItem,
  type StrandedAvatarItem,
} from '../api/adminOps';
import {
  QueueTable,
  StatusChip,
  DetailDrawer,
  ReasonActionBar,
  type ChipTone,
  type QueueColumn,
} from '../components/ui';

const TABS = [
  { id: 'quarantine', label: 'Quarantine' },
  { id: 'outbox', label: 'Failed outbox' },
  { id: 'avatars', label: 'Stranded avatars' },
  { id: 'org-status', label: 'Org-status backlog' },
] as const;
type TabId = (typeof TABS)[number]['id'];

const DISCARD_REASONS = [
  { value: 'spam', label: 'Spam / unsolicited' },
  { value: 'phishing_or_malware', label: 'Phishing or malware' },
  { value: 'not_actionable', label: 'Not actionable' },
  { value: 'duplicate', label: 'Duplicate of a handled message' },
];

const REPLAY_REASONS = [
  { value: 'transient_failure_resolved', label: 'Transient failure resolved' },
  { value: 'downstream_fix_deployed', label: 'Downstream fix deployed' },
  { value: 'manual_verification_done', label: 'Manually verified safe to replay' },
];

const MAX_REPLAY_SELECTION = 50;

function relativeTime(iso: string | null) {
  return iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : '—';
}

function ErrorCard({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div className="glass-card p-6 border border-red-500/30 flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-white font-medium mb-1">{title}</p>
        <p className="text-sm text-gray-text mb-3">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <button onClick={onRetry} className="btn-secondary">
          Retry
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Quarantine tab
// ============================================================================

function QuarantineTab() {
  const queue = useQuarantine();
  const discardMutation = useDecideQuarantine();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selected = queue.rows.find((row) => row.id === selectedId) ?? null;
  const closeDrawer = () => setSelectedId(null);

  const columns: QueueColumn<QuarantineItem>[] = [
    {
      key: 'received',
      header: 'Received',
      render: (row) => (
        <span className="text-gray-text whitespace-nowrap">{relativeTime(row.created_at)}</span>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      render: (row) => <span className="text-white">{row.subject ?? '(no subject)'}</span>,
    },
    {
      key: 'from_domain',
      header: 'Sender domain',
      render: (row) => <span className="text-gray-text">{row.from_domain ?? '—'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusChip label={row.status} tone="warn" />,
    },
    {
      key: 'error',
      header: 'Error',
      render: (row) => (
        <span className="text-gray-text text-xs">
          {row.error_code ?? '—'}
          {row.error_message && (
            <span className="block text-gray-text/80 truncate max-w-[280px]">
              {row.error_message}
            </span>
          )}
        </span>
      ),
    },
  ];

  if (queue.isError) {
    return (
      <ErrorCard
        title="Failed to load quarantined emails"
        error={queue.error}
        onRetry={() => queue.refetch()}
      />
    );
  }

  return (
    <>
      <QueueTable
        columns={columns}
        rows={queue.rows}
        rowKey={(row) => row.id}
        isLoading={queue.isPending}
        onRowClick={(row) => setSelectedId(row.id)}
        emptyTitle="Quarantine is empty"
        emptyHint="No inbound emails are held for review."
        hasNextPage={queue.hasNextPage}
        isFetchingNextPage={queue.isFetchingNextPage}
        onLoadMore={() => queue.fetchNextPage()}
      />
      <DetailDrawer
        open={selected !== null}
        title={selected?.subject ?? 'Quarantined email'}
        onClose={closeDrawer}
        footer={
          selected && (
            <ReasonActionBar
              reasons={DISCARD_REASONS}
              actionLabel="Discard email"
              destructive
              isPending={discardMutation.isPending}
              onSubmit={(reason, note) => {
                discardMutation.mutate(
                  { id: selected.id, reason: note ? `${reason}: ${note}` : reason },
                  { onSuccess: closeDrawer }
                );
              }}
            />
          )
        }
      >
        {selected && (
          <>
            <div className="flex items-center gap-2">
              <StatusChip label={selected.status} tone="warn" />
            </div>
            <div className="space-y-1 text-sm">
              <p className="text-gray-text">
                Received {new Date(selected.created_at).toLocaleString()}
              </p>
              <p className="text-gray-text">
                Sender domain: <span className="text-white">{selected.from_domain ?? '—'}</span>
              </p>
              <p className="text-gray-text">
                Error code: <span className="text-white">{selected.error_code ?? '—'}</span>
              </p>
              {selected.error_message && (
                <p className="text-white break-words">{selected.error_message}</p>
              )}
            </div>
            <p className="text-xs text-gray-text border-t border-gray-border pt-3">
              Only the sender's domain is exposed (never the full address). Discarding
              permanently removes the message from processing.
            </p>
          </>
        )}
      </DetailDrawer>
    </>
  );
}

// ============================================================================
// Failed outbox tab
// ============================================================================

function FailedOutboxTab() {
  const queue = useFailedOutbox();
  const replayMutation = useReplayOutbox();
  const replayAllMutation = useReplayAllOutbox();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [drawer, setDrawer] = useState<'selected' | 'all' | null>(null);

  const toggleRow = (row: FailedOutboxItem) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else if (next.size < MAX_REPLAY_SELECTION) {
        next.add(row.id);
      }
      return next;
    });
  };

  const closeDrawer = () => setDrawer(null);

  const columns: QueueColumn<FailedOutboxItem>[] = [
    {
      key: 'select',
      header: '',
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.has(row.id)}
          onChange={() => toggleRow(row)}
          onClick={(event) => event.stopPropagation()}
          className="accent-primary w-4 h-4"
          aria-label={`Select event ${row.id}`}
        />
      ),
    },
    {
      key: 'event_type',
      header: 'Event type',
      render: (row) => (
        <div>
          <p className="text-white">{row.event_type}</p>
          <p className="text-xs text-gray-text mt-0.5">
            EIN {row.ein} · {row.old_status ?? '∅'} → {row.new_status}
          </p>
        </div>
      ),
    },
    {
      key: 'failed_at',
      header: 'Failed',
      render: (row) => (
        <span className="text-gray-text whitespace-nowrap">{relativeTime(row.created_at)}</span>
      ),
    },
    {
      key: 'attempts',
      header: 'Attempts',
      render: (row) => {
        const tone: ChipTone = row.retry_count >= row.max_retries ? 'crit' : 'warn';
        return <StatusChip label={`${row.retry_count}/${row.max_retries}`} tone={tone} />;
      },
    },
    {
      key: 'last_error',
      header: 'Last error',
      render: (row) => (
        <span className="text-gray-text text-xs truncate block max-w-[280px]">
          {row.last_error ?? '—'}
        </span>
      ),
    },
  ];

  if (queue.isError) {
    return (
      <ErrorCard
        title="Failed to load the outbox backlog"
        error={queue.error}
        onRetry={() => queue.refetch()}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-text">
          {selectedIds.size}/{MAX_REPLAY_SELECTION} selected
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDrawer('selected')}
            disabled={selectedIds.size === 0}
            className="btn-secondary disabled:opacity-50"
          >
            Replay selected
          </button>
          <button onClick={() => setDrawer('all')} className="btn-primary">
            Replay all…
          </button>
        </div>
      </div>
      <QueueTable
        columns={columns}
        rows={queue.rows}
        rowKey={(row) => row.id}
        isLoading={queue.isPending}
        onRowClick={toggleRow}
        emptyTitle="No failed outbox events"
        emptyHint="Every organization status event has been delivered."
        hasNextPage={queue.hasNextPage}
        isFetchingNextPage={queue.isFetchingNextPage}
        onLoadMore={() => queue.fetchNextPage()}
      />
      <DetailDrawer
        open={drawer === 'selected'}
        title={`Replay ${selectedIds.size} selected event(s)`}
        onClose={closeDrawer}
        footer={
          <ReasonActionBar
            reasons={REPLAY_REASONS}
            actionLabel={`Replay ${selectedIds.size} event(s)`}
            isPending={replayMutation.isPending}
            onSubmit={(reason, note) => {
              replayMutation.mutate(
                {
                  ids: Array.from(selectedIds),
                  reason: note ? `${reason}: ${note}` : reason,
                },
                {
                  onSuccess: () => {
                    setSelectedIds(new Set());
                    closeDrawer();
                  },
                }
              );
            }}
          />
        }
      >
        <p className="text-sm text-gray-text">
          The selected events are re-published immediately (no approval step). Replay only after
          confirming the downstream failure is fixed.
        </p>
      </DetailDrawer>
      <DetailDrawer
        open={drawer === 'all'}
        title="Replay entire failed outbox"
        onClose={closeDrawer}
        footer={
          <ReasonActionBar
            reasons={REPLAY_REASONS}
            actionLabel="Request replay-all"
            destructive
            isPending={replayAllMutation.isPending}
            onSubmit={(reason, note) => {
              replayAllMutation.mutate(
                { reason: note ? `${reason}: ${note}` : reason },
                { onSuccess: closeDrawer }
              );
            }}
          />
        }
      >
        <p className="text-sm text-gray-text">
          Replaying the entire backlog is <span className="text-white">dual-controlled</span>: the
          request returns 202 and lands in the Approvals queue as a pending action. Nothing is
          replayed until a second admin approves it.
        </p>
      </DetailDrawer>
    </>
  );
}

// ============================================================================
// Read-only tabs
// ============================================================================

function StrandedAvatarsTab() {
  const queue = useStrandedAvatars();

  const columns: QueueColumn<StrandedAvatarItem>[] = [
    {
      key: 'user_id',
      header: 'User',
      render: (row) => (
        <span className="text-white font-mono text-xs break-all">{row.user_id}</span>
      ),
    },
    {
      key: 'age',
      header: 'Candidate age',
      render: (row) => (
        <span className="text-gray-text whitespace-nowrap">
          {relativeTime(row.avatar_candidate_updated_at)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.avatar_candidate_status ? (
          <StatusChip label={row.avatar_candidate_status} tone="warn" />
        ) : (
          <span className="text-gray-text">—</span>
        ),
    },
  ];

  if (queue.isError) {
    return (
      <ErrorCard
        title="Failed to load stranded avatars"
        error={queue.error}
        onRetry={() => queue.refetch()}
      />
    );
  }

  return (
    <QueueTable
      columns={columns}
      rows={queue.rows}
      rowKey={(row) => row.user_id}
      isLoading={queue.isPending}
      emptyTitle="No stranded avatars"
      emptyHint="Every avatar upload finished promotion or cleanup."
      hasNextPage={queue.hasNextPage}
      isFetchingNextPage={queue.isFetchingNextPage}
      onLoadMore={() => queue.fetchNextPage()}
    />
  );
}

function OrgStatusBacklogTab() {
  const queue = useOrgStatusUnacknowledged();

  const columns: QueueColumn<OrgStatusUnacknowledgedItem>[] = [
    {
      key: 'donation_id',
      header: 'Donation',
      render: (row) => (
        <span className="text-white font-mono text-xs break-all">{row.donation_id}</span>
      ),
    },
    {
      key: 'organization',
      header: 'Organization',
      render: (row) => (
        <span className="text-gray-text font-mono text-xs break-all">
          {row.organization_id ?? '—'}
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Warning reason',
      render: (row) =>
        row.compliance_warning_reason ? (
          <StatusChip label={row.compliance_warning_reason} tone="warn" />
        ) : (
          <span className="text-gray-text">—</span>
        ),
    },
    {
      key: 'set_at',
      header: 'Set',
      render: (row) => (
        <span className="text-gray-text whitespace-nowrap">
          {relativeTime(row.compliance_warning_set_at)}
        </span>
      ),
    },
  ];

  if (queue.isError) {
    return (
      <ErrorCard
        title="Failed to load the org-status backlog"
        error={queue.error}
        onRetry={() => queue.refetch()}
      />
    );
  }

  return (
    <QueueTable
      columns={columns}
      rows={queue.rows}
      rowKey={(row) => row.donation_id}
      isLoading={queue.isPending}
      emptyTitle="No unacknowledged warnings"
      emptyHint="Every donor has seen their organization compliance warnings."
      hasNextPage={queue.hasNextPage}
      isFetchingNextPage={queue.isFetchingNextPage}
      onLoadMore={() => queue.fetchNextPage()}
    />
  );
}

// ============================================================================
// Page
// ============================================================================

const TAB_SUBTITLES: Record<TabId, string | null> = {
  quarantine: null,
  outbox: null,
  avatars:
    'Read-only for now — acting on stranded candidates (promote / clean up) is a follow-up.',
  'org-status':
    'Read-only for now — nudging donors or acknowledging on their behalf is a follow-up.',
};

export default function OperationsQueues() {
  const [tab, setTab] = useState<TabId>('quarantine');

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Layers className="w-7 h-7 text-primary" />
          Operations queues
        </h1>
        <p className="text-gray-text">
          Background-pipeline backlogs: quarantined email, failed outbox events, and cleanup
          queues.
        </p>
      </div>

      {/* Tabs */}
      <div className="glass-card p-2 flex items-center gap-1 flex-wrap">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            onClick={() => setTab(entry.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === entry.id
                ? 'bg-primary/20 text-primary'
                : 'text-gray-text hover:text-white'
            }`}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {TAB_SUBTITLES[tab] && <p className="text-sm text-gray-text">{TAB_SUBTITLES[tab]}</p>}

      {tab === 'quarantine' && <QuarantineTab />}
      {tab === 'outbox' && <FailedOutboxTab />}
      {tab === 'avatars' && <StrandedAvatarsTab />}
      {tab === 'org-status' && <OrgStatusBacklogTab />}
    </div>
  );
}
